import { useEffect } from "react";
import { useForm, RegisterOptions } from "react-hook-form";
import { Survey, WithId, Viewer } from "@kaigi100-next/typing";
import { Scenario } from "@kaigi100-next/typing/survey";
import { ErrorMessage } from "components/form/error-message";
import { ViewerNameField } from "page-components/user/edit/viewer-name-field";
import { Button } from "components/button";
import { isOtherAnswer } from "./utility";
type FormValues = {
  name: string;
  surveyAnswers: {
    [surveyId: string]: { answer: string; answerId?: string; other?: string };
  };
};

export function Form({
  surveys,
  surveyAnswers,
  viewer,
  onSubmit,
  submitProcessing,
  scenario = "before_session",
  buttonText = "登録する",
  flag = false,
}: {
  surveys: WithId<Survey>[];
  surveyAnswers: WithId<SurveyAnswer>[];
  viewer: Viewer;
  onSubmit: (values: FormValues) => void;
  submitProcessing: boolean;
  scenario?: Scenario;
  buttonText?: string;
  flag?: boolean;
}) {
  const { register, handleSubmit, errors, reset } = useForm<FormValues>({
    reValidateMode: "onBlur",
  });
  useEffect(() => {
    const answered = surveyAnswers.length > 0;
    /**
     * 会議前アンケート回答済みの場合
     * 回答内容をフォームの初期値に設定
     */
    if (answered) {
      reset({
        name: viewer.name,
        surveyAnswers: surveyAnswers.reduce((prev, surveyAnswer) => {
          const {
            id: answerId,
            answer,
            indexes: { surveyId },
          } = surveyAnswer;
          const survey = surveys.find((survey) => survey.id === surveyId);
          if (!survey) {
            prev["dummyId"] = { answer: "test", answerId: "test" };
            return prev;
          }
          if (isOtherAnswer(survey, answer)) {
            prev[surveyId] = {
              answer: "その他",
              answerId,
              other: surveyAnswer.answer,
            };
          } else {
            prev[surveyId] = {
              answer,
              answerId,
            };
          }
          return prev;
        }, {}),
      });
      return;
    }
    /**
     * 会議前アンケート回答前の場合
     * 研究会ホームページ連携データがある場合は、フォームの初期値に設定
     */
    const {
      clubResponseSnapshot: { occupation, workPlace },
    } = viewer;
    // 連携データがない場合は名前だけセット
    if (occupation === "" && workPlace === "") {
      reset({
        name: viewer.name,
      });
      return;
    }
    const resetData = {};
    if (occupation !== "") {
      const occupationSurvey = surveys.find(
        (survey) => survey.clubAttributeSlug === "occupation"
      );
      if (occupationSurvey) {
        resetData[occupationSurvey.id] = { answer: occupation };
      }
    }
    if (workPlace !== "") {
      const workPlaceSurvey = surveys.find(
        (survey) => survey.clubAttributeSlug === "workPlace"
      );
      if (workPlaceSurvey) {
        resetData[workPlaceSurvey.id] = { answer: workPlace };
      }
    }
    reset({
      name: viewer.name,
      surveyAnswers: resetData,
    });
  }, [surveys, surveyAnswers]);
  return (
    <form
      onSubmit={handleSubmit((data) => {
        const answers = Object.keys(data.surveyAnswers ?? []).reduce(
          (prev, surveyId) => {
            const { answerId, answer, other } = data.surveyAnswers[surveyId];
            const survey = surveys.find((survey) => survey.id === surveyId);
            if (isOtherAnswer(survey, answer)) {
              prev[surveyId] = { answerId, answer: other };
            } else {
              prev[surveyId] = { answerId, answer };
            }
            return prev;
          },
          {}
        );
        onSubmit({ name: data.name, surveyAnswers: answers });
      })}
    >
      <div className="mx-3 divide-y divide-gray-600">
        {scenario === "before_session" && surveys.length === 0 && (
          <>
            <ViewerNameField
              option={viewer.participatingConference.viewerNameFieldOption}
              inputRef={register({
                required: `「${
                  viewer.participatingConference.viewerNameFieldOption.label ||
                  "ニックネーム"
                }」を入力してください。`,
              })}
            />
            {errors.name?.message && (
              <ErrorMessage>{errors.name.message}</ErrorMessage>
            )}
          </>
        )}
        {surveys.map((survey, index) => {
          const validationRules: RegisterOptions = {};
          if (survey.required) {
            validationRules.required = `「${survey.name}」を入力してください。`;
          }
          if (survey.optionalControlCode === "five_number") {
            validationRules.pattern = {
              value: /[\d]{5}/,
              message: `「${survey.name}」は5文字の数字で入力してください。`,
            };
          }
          return (
            <div key={survey.id} className="divide-y divide-gray-600">
              {scenario === "before_session" &&
                index ==
                  viewer.participatingConference.viewerNameFieldOption
                    .displayIndex && (
                  <>
                    <ViewerNameField
                      option={
                        viewer.participatingConference.viewerNameFieldOption
                      }
                      inputRef={register({
                        required: `「${
                          viewer.participatingConference.viewerNameFieldOption
                            .label || "ニックネーム"
                        }」を入力してください。`,
                      })}
                    />
                    {errors.name?.message && (
                      <ErrorMessage>{errors.name.message}</ErrorMessage>
                    )}
                  </>
                )}
              <div className="py-4 px-2">
                <div className="text-gray-800 text-md font-bold mb-1 flex items-center">
                  {survey.required ? (
                    <span className="font-normal text-xs px-2 rounded-full bg-red-600 mr-2 text-white">
                      必須
                    </span>
                  ) : (
                    <span className="font-normal text-xs px-2 rounded-full bg-green-600 mr-2 text-white">
                      任意
                    </span>
                  )}
                  <p className="flex-1">{survey.name}</p>
                </div>
                <input
                  type="hidden"
                  name={`surveyAnswers[${survey.id}].surveyId`}
                  value={survey.id}
                  ref={register}
                />
                <input
                  type="hidden"
                  name={`surveyAnswers[${survey.id}].answerId`}
                  ref={register}
                />
                {survey.answerFormat === "string" && (
                  <input
                    type="text"
                    name={`surveyAnswers[${survey.id}].answer`}
                    ref={register(validationRules)}
                    className="w-full appearance-none border rounded-sm bg-gray-100 text-base outline-none py-2 px-4 mb-3"
                    data-test={`survey-input-${survey.id}`}
                  />
                )}
                {survey.answerFormat === "text" && (
                  <textarea
                    name={`surveyAnswers[${survey.id}].answer`}
                    ref={register(validationRules)}
                    className="w-full appearance-none border rounded-sm bg-gray-100 text-base outline-none py-2 px-4 mb-3"
                    data-test={`survey-input-${survey.id}`}
                  />
                )}
                {survey.answerFormat === "pulldown" && (
                  <select
                    className="block appearance-none w-full bg-gray-200 border border-gray-200 text-gray-700 py-3 px-4 pr-8 rounded leading-tight focus:outline-none focus:bg-white focus:border-gray-500 mb-3"
                    name={`surveyAnswers[${survey.id}].answer`}
                    ref={register(validationRules)}
                    data-test={`survey-input-${survey.id}`}
                  >
                    <option value="">リストから選択</option>
                    {survey.choices.map((choice) => (
                      <option key={choice} value={choice}>
                        {choice}
                      </option>
                    ))}
                  </select>
                )}
                {survey.answerFormat === "radio" && (
                  <>
                    {survey.choices.map((choice) => (
                      <div key={`survey-choice-${choice}`}>
                        <label className="inline-flex items-center">
                          <input
                            type="radio"
                            className="form-radio"
                            name={`surveyAnswers[${survey.id}].answer`}
                            value={choice}
                            ref={register(validationRules)}
                            data-test={`survey-input-${survey.id}`}
                          />
                          <span className="ml-2">{choice}</span>
                        </label>
                      </div>
                    ))}
                    {survey.acceptOtherAnswer && (
                      <div>
                        <label className="inline-flex items-center">
                          <input
                            type="radio"
                            className="form-radio"
                            name={`surveyAnswers[${survey.id}].answer`}
                            value="その他"
                            ref={register(validationRules)}
                            data-test={`survey-input-${survey.id}`}
                          />
                          <span className="ml-2">その他</span>
                        </label>
                        <div>
                          <input
                            type="text"
                            name={`surveyAnswers[${survey.id}].other`}
                            ref={register()}
                            className="w-full appearance-none border rounded-sm bg-gray-100 text-base outline-none py-2 px-4 mb-3"
                            data-test={`survey-input-${survey.id}-other`}
                          />
                        </div>
                      </div>
                    )}
                  </>
                )}
                {survey.answerFormat === "checkbox" && (
                  <>
                    {survey.choices.map((choice) => (
                      <>
                        <label className="inline-flex items-center">
                          <input
                            type="checkbox"
                            name={`surveyAnswers[${survey.id}].answer`}
                            value={choice}
                            ref={register(validationRules)}
                            data-test={`survey-input-${survey.id}`}
                          />
                          <span className="ml-2">{choice}</span>
                        </label>
                      </>
                    ))}
                  </>
                )}
                {errors.surveyAnswers?.[survey.id]?.answer?.message && (
                  <ErrorMessage>
                    {errors.surveyAnswers[survey.id].answer.message}
                  </ErrorMessage>
                )}
              </div>
            </div>
          );
        })}
      </div>
      {flag && (
        <section className="mt-2 mb-6 mx-3 text-gray-600 p-4 bg-green-50 text-sm">
          <div className="space-y-2 mb-4">
            <p>
              本アンケートの情報は、バイエル薬品の今後のコングレス活動の企画・運営の参考資料として利用し、その他の目的には使用致しません。
            </p>
            <p>
              また、法令で許される場合を除き、個人情報を社外に 提供致しません。
            </p>
            <p>
              尚、個人情報を含む本アンケートの回答内容につきましては、弊社が契約を締結した外部委託業者においても集計のため参照されます。
            </p>
            <p>
              上記事項にご理解、ご同意の上、ご回答いただきますよう宜しくお願い申し上げます。
            </p>
          </div>
          <p>バイエル薬品 マーケットアクセス本部 本部長 相徳 泰子</p>
        </section>
      )}
      <div className="px-3 py-1">
        <Button loading={submitProcessing} testSelector="survey-submit">
          {buttonText}
        </Button>
      </div>
    </form>
  );
}
